<template>
    <div class="graph-pie">
        <canvas ref="canvas"></canvas>
    </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { mapState } from 'pinia';
import { Chart, registerables } from 'chart.js';
import { defineComponent } from 'vue';
import { useScreenWidthStore } from '@/stores/utils/screenWidth';

Chart.register(...registerables);

export default defineComponent({
  name: 'GraphPie',
  props: {
    chartData: {
      type: Object,
      required: true
    },
    chartOptions: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    ...mapState(useScreenWidthStore, ['hasMediumScreen']),
  },
  data() {
    return {
      chart: null,
      debouncedUpdateChart: null
    };
  },
  created() {
    this.debouncedUpdateChart = debounce(this.updateChart, 300);
  },
  mounted() {
    this.createChart();
  },
  unmounted() {
    if (this.debouncedUpdateChart && this.debouncedUpdateChart.cancel) {
      this.debouncedUpdateChart.cancel();
    }
    this.destroyChart();
  },
  watch: {
    chartData: {
      handler() {
        this.debouncedUpdateChart();
      },
      deep: true
    },
    chartOptions: {
      handler() {
        this.destroyChart();
        this.createChart();
      },
      deep: true
    },
    hasMediumScreen() {
      this.destroyChart();
      this.createChart();
    }
  },
  methods: {
    createChart() {
      try {
        const ctx = this.$refs.canvas.getContext('2d');
        const modifiedData = {
          ...this.chartData,
          datasets: this.chartData.datasets.map(dataset => ({
            ...dataset,
            borderWidth: 0
          }))
        };
        this.chart = new Chart(ctx, {
          type: 'doughnut',
          data: modifiedData,
          options: {
            responsive: true,
            plugins: {
              legend: { display: false },
              tooltip: {
                enabled: this.hasMediumScreen,
                backgroundColor: 'white',
                titleColor: 'black',
                bodyColor: 'black',
                padding: 15,
                cornerRadius: 2,
                titleFont: { size: 14 },
                bodyFont: { size: 13 },
              }
            },
            ...this.chartOptions
          }
        });
      } catch (error) {
        console.error('Error creating chart:', error);
      }
    },
    updateChart() {
      if (!this.chart) return;
      try {
        const modifiedData = {
          ...this.chartData,
          datasets: this.chartData.datasets.map(dataset => ({
            ...dataset,
            borderWidth: 0
          }))
        };
        this.chart.data = modifiedData;
        this.chart.update();
      } catch (error) {
        console.error('Error updating chart:', error);
        this.destroyChart();
        this.createChart();
      }
    },
    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    }
  }
});
</script>

<style scoped>
  canvas {
    width: 100% !important;
    height: 100% !important;
  }
</style>
